import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, TrendUp, Heart, Lock, Compass, Gift } from "@phosphor-icons/react";

export function InsightsView({ isPro, onUpgrade, comp, onOpenComplimentary }) {
  const [data, setData] = useState(null);
  const [deep, setDeep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);

  useEffect(() => {
    api.get("/insights")
      .then((r) => setData(r.data))
      .catch(() => setData({ ready: false, memory_count: 0 }))
      .finally(() => setLoading(false));
  }, []);

  const generateDeep = async () => {
    if (!isPro) return onUpgrade?.();
    setGen(true);
    try {
      const r = await api.post("/insights/deep");
      setDeep(r.data.insight);
    } catch (e) {
      if (e.response?.status === 402) onUpgrade?.();
      else toast.error("Could not generate your insight.");
    } finally {
      setGen(false);
    }
  };

  if (loading) return <p className="text-[#8A7F76]">Reading your story…</p>;

  if (!data?.ready) {
    const pct = Math.min(100, ((data?.memory_count || 0) / 3) * 100);
    return (
      <div className="mx-auto max-w-md rounded-2xl border border-[#4A4540]/10 bg-white p-8 text-center shadow-sm" data-testid="insights-locked">
        <Compass size={40} weight="duotone" className="mx-auto text-[#B08D57]" />
        <h3 className="mt-4 font-serif text-2xl text-[#2C2A28]">Your Mini Self-Portrait</h3>
        <p className="mt-2 text-[#8A7F76]">
          Add {3 - (data?.memory_count || 0)} more{" "}
          {3 - (data?.memory_count || 0) === 1 ? "memory" : "memories"} and we'll
          gently show you the strengths and values already running through your story.
        </p>
        <Progress value={pct} className="mt-5 h-2" />
        <p className="mt-2 text-xs text-[#8A7F76]">{data?.memory_count || 0} of 3 memories</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="insights-ready">
      {/* Free teaser: Mini Self-Portrait */}
      <div className="rounded-2xl border border-[#7EA193]/30 bg-[#EAF1EE] p-6">
        <div className="flex items-center gap-2 text-[#4A6B5D]">
          <Sparkle size={18} weight="duotone" />
          <span className="text-sm font-medium uppercase tracking-wide">Your Mini Self-Portrait</span>
        </div>
        {data.portrait_line && (
          <p className="mt-3 font-handwriting text-3xl leading-snug text-[#2C2A28]">{data.portrait_line}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-[#4A4540]/10 bg-white p-5">
          <div className="mb-3 flex items-center gap-2 text-[#7EA193]">
            <TrendUp size={18} weight="duotone" /> <span className="text-sm font-medium">Recurring strengths</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.top_strengths.length ? data.top_strengths.map((s) => (
              <span key={s.name} data-testid={`insight-strength-${s.name}`} className="rounded-full bg-[#7EA193]/20 px-3 py-1 text-sm text-[#4A6B5D]">
                {s.name} · {s.count}
              </span>
            )) : <span className="text-sm text-[#8A7F76]">Tag strengths on your memories to see these.</span>}
          </div>
        </div>
        <div className="rounded-xl border border-[#4A4540]/10 bg-white p-5">
          <div className="mb-3 flex items-center gap-2 text-[#B08D57]">
            <Heart size={18} weight="duotone" /> <span className="text-sm font-medium">How you've felt</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.top_feelings.length ? data.top_feelings.map((f) => (
              <span key={f.name} className="rounded-full bg-[#DDA77B]/25 px-3 py-1 text-sm text-[#8A5A2B]">{f.name}</span>
            )) : <span className="text-sm text-[#8A7F76]">Add feelings to your memories.</span>}
          </div>
        </div>
        <div className="rounded-xl border border-[#4A4540]/10 bg-white p-5">
          <div className="mb-3 flex items-center gap-2 text-[#8A5A2B]">
            <Compass size={18} weight="duotone" /> <span className="text-sm font-medium">Growth</span>
          </div>
          <p className="text-sm leading-relaxed text-[#4A4540]">
            {data.growth_highlight || "As you record more, we'll show how you've grown over time."}
          </p>
        </div>
      </div>

      {/* Complimentary gift for trial users */}
      {!isPro && comp && (comp.eligible || comp.claimed) && (
        <div
          data-testid="complimentary-insight-card"
          className="rounded-2xl border border-[#B08D57]/40 bg-[#F4EFE6] p-6"
        >
          <div className="flex items-center gap-2 text-[#B08D57]">
            <Gift size={20} weight="duotone" />
            <span className="text-sm font-medium uppercase tracking-wide">Your complimentary Memory Insight</span>
          </div>
          <p className="mt-2 text-[#4A4540]">
            {comp.claimed
              ? "It's saved with the Childhood memory you chose — open it to reread or download."
              : "You've written five Childhood memories, so your first Memory Insight is our gift. Choose any Childhood memory."}
          </p>
          <Button
            data-testid="open-complimentary-btn"
            onClick={onOpenComplimentary}
            className="mt-4 rounded-full bg-[#B08D57] text-[#FDFBF7] hover:-translate-y-0.5 hover:bg-[#C19A6B]"
          >
            <Sparkle size={16} weight="duotone" className="mr-1" />
            {comp.claimed ? "Open my Memory Insight" : "Choose a Childhood memory"}
          </Button>
        </div>
      )}

      {/* Paid deep insight */}
      <div className="rounded-2xl border border-[#B08D57]/30 bg-white p-6">
        <div className="flex items-center gap-2">
          <Sparkle size={20} weight="duotone" className="text-[#B08D57]" />
          <h3 className="font-serif text-xl text-[#2C2A28]">Your full life insight</h3>
        </div>
        {deep ? (
          <p data-testid="deep-insight-text" className="mt-3 font-handwriting text-2xl leading-snug text-[#4A4540]">{deep}</p>
        ) : (
          <p className="mt-2 text-sm text-[#8A7F76]">
            A warm, written reflection that draws together everything you've recorded —
            your strengths, your lessons, and the growth across your journey.
          </p>
        )}
        <Button
          data-testid="generate-insight-btn"
          onClick={generateDeep}
          disabled={gen}
          className="mt-4 rounded-full bg-[#B08D57] text-[#FDFBF7] hover:-translate-y-0.5 hover:bg-[#C19A6B]"
        >
          {isPro ? <Sparkle size={16} weight="duotone" className="mr-1" /> : <Lock size={14} weight="fill" className="mr-1" />}
          {gen ? "Reflecting on your life…" : deep ? "Refresh insight" : isPro ? "Write my life insight" : "Unlock full insight"}
        </Button>
      </div>
    </div>
  );
}
